/**
 * frontend/src/lib/i18n.ts
 * `#796` — **L'i18n DU FRONT, INITIALISÉE DEPUIS LE CATALOGUE PARTAGÉ.**
 *
 * ⚠⚠ **UNE SEULE VÉRITÉ** : les ressources viennent de `@i18n` (le catalogue
 *   `src/shared/i18n`, partagé back+front). Pas de second dictionnaire côté
 *   front. `keySeparator: false` — nos clés portent des points (`nav.marche`),
 *   ce sont des clés **littérales**, pas des chemins de namespace.
 *
 * ⚠ **CLÉS TYPÉES** : `useT()` rend un `t(cle: CleT)` — une clé absente est
 *   une **erreur de compilation**, jamais un `[missing]` à l'écran.
 */
import i18next from 'i18next'
import { initReactI18next, useTranslation } from 'react-i18next'
import { fr, en, type CleT } from '@i18n'

/** ⚠ *Défaut `fr` : tant que le compte n'a rien dit, l'écran reste comme hier
 *   (`Δ = 0`). `/auth/me.language` bascule ensuite via `appliquerLangue`.* */
void i18next.use(initReactI18next).init({
  resources: {
    fr: { translation: fr },
    en: { translation: en },
  },
  lng: 'fr',
  fallbackLng: 'fr',
  keySeparator: false,
  nsSeparator: false,
  interpolation: { escapeValue: false },
})

export type Langue = 'fr' | 'en'

/**
 * `#801` — Traducteur **FR par défaut** pour les helpers purs (hors composant) :
 * `libellePortee`, `libellePeriode`, `titrePourMode`… reçoivent un `t` dont le
 * défaut rend le FR du catalogue → **Δ=0** partout où le composant n'injecte pas
 * le vrai `t` (tests, appelants non i18n).
 */
/**
 * ⚠⚠ `#842` — **`trFr` INTERPOLE MAINTENANT, ET IL LE FALLAIT.** *Il rendait le
 *    gabarit BRUT : une clé à `{{nom}}` sortait `« {{nom}} » est déjà la règle
 *    active`. Tant que les helpers purs ne portaient que des clés SANS
 *    interpolation (`#805`, `format.ts`), le défaut tenait ; les textes de
 *    confirmation de segmentation, eux, nomment la règle. **Un défaut qui ne
 *    couvre pas tous les cas de ses appelants est un piège, pas un défaut.**
 *    Même substitution que `useT` (`escapeValue: false`), et sans paramètres le
 *    résultat est byte-pour-byte celui d'avant.*
 */
export const trFr = (cle: CleT, params?: Record<string, string | number>): string => {
  /**
   * ⚠ `#861` — **`trFr` NE PLURALISE PAS.** *Une clé pluralisée adressée par sa
   *   base ne vit dans aucune table ; ce défaut rend alors la clé, ce que le
   *   témoin « clé nue » (`#850`) voit. Les six clés concernées sont appelées
   *   depuis des composants, avec `useT()` — jamais par ce repli.*
   */
  const brut = fr[cle as keyof typeof fr] ?? cle
  if (!params) return brut
  return brut.replace(/\{\{(\w+)\}\}/g, (tel, nom: string) =>
    Object.prototype.hasOwnProperty.call(params, nom) ? String(params[nom]) : tel)
}

/** Bascule la langue de l'interface (re-rendu immédiat, sans rechargement). */
export function appliquerLangue(langue: Langue): void {
  void i18next.changeLanguage(langue)
}

/** Paramètres d'interpolation i18next (`{{clé}}` dans le libellé). */
export type ParamsI18n = Record<string, string | number>

/**
 * Hook de traduction **typé** sur le catalogue. `t('nav.marche')` compile ;
 * `t('nav.inexistant')` **ne compile pas**. Deuxième argument optionnel pour
 * l'interpolation (`t('marche.carte.moyAgg', { x, n })` → `{{x}}`/`{{n}}`).
 */
export function useT(): (cle: CleT, params?: ParamsI18n) => string {
  const { t } = useTranslation()
  return (cle: CleT, params?: ParamsI18n) => t(cle, params ?? {})
}

export { i18next }
